export default {
  // Plugins
  async getPlugins(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/plugins${query ? '?' + query : ''}`);
  },

  async getPlugin(pluginId) {
    return this.request(`/plugins/${pluginId}`);
  },

  // `groupId` asks for that group's plugins instead of your personal ones —
  // the same shape as `/third-party/my-apps?group_id=`. Without it a plugin
  // transferred to a group has nowhere left to appear.
  async getMyPlugins(groupId) {
    const response = await this.request(
      groupId ? `/plugins/my-plugins?group_id=${encodeURIComponent(groupId)}` : '/plugins/my-plugins'
    );
    return response.plugins || [];
  },

  async registerPlugin(data) {
    return this.request('/plugins/register', {
      method: 'POST',
      body: data,
    });
  },

  async updatePlugin(pluginId, updates) {
    return this.request(`/plugins/${pluginId}`, {
      method: 'PUT',
      body: updates,
    });
  },

  async deletePlugin(pluginId) {
    return this.request(`/plugins/${pluginId}`, {
      method: 'DELETE',
    });
  },

  async mcpDiscover(mcpServerUrl) {
    return this.request('/plugins/mcp-discover', {
      method: 'POST',
      body: { mcp_server_url: mcpServerUrl },
    });
  },

  // ── The plugin workspace ────────────────────────────────────────────────
  // What each part of the plugin asks for: {parts:[{tool_id, name,
  // irreversible, reads, writes, outside, goes_to, removes_source,
  // asks_every_run}]}.
  async getAsksFor(pluginId) {
    return this.request(`/plugins/${pluginId}/asks-for`);
  },

  // What a save WOULD change, without saving: {valid, errors, changes,
  // reapproval, approvals_lapsing, asks_for}. The body is the PUT's body.
  async previewChanges(pluginId, body) {
    return this.request(`/plugins/${pluginId}/changes-preview`, {
      method: 'POST',
      body,
    });
  },

  // One tool, called for real, with nothing kept: {outcome, kept, left_out,
  // refused, response}. Body: {tool_id, input_parameters, settings?}.
  async testRun(pluginId, body) {
    return this.request(`/plugins/${pluginId}/test-run`, {
      method: 'POST',
      body,
    });
  },

  // {plugin_id, plugin_secret, rotated_at} — the secret is in this response only.
  async rotateSecret(pluginId) {
    return this.request(`/plugins/${pluginId}/rotate-secret`, { method: 'POST' });
  },

  async requestVerification(pluginId) {
    return this.request(`/plugins/${pluginId}/verification-request`, { method: 'POST' });
  },

  async getHealth(pluginId) {
    return this.request(`/plugins/${pluginId}/health`);
  },

  // The tools this plugin has registered. `groupId` reads them as that group.
  async getRegisteredTools(pluginId, groupId) {
    return this.request(
      groupId
        ? `/groups/${encodeURIComponent(groupId)}/plugins/${pluginId}/tools`
        : `/plugins/${pluginId}/tools`
    );
  },

  async getManifest(pluginId) {
    return this.request(`/plugins/${pluginId}/manifest`);
  },

  // The request a declarative ("request") plugin would make, without making
  // it. `params` may carry tool_id, group_id and field values.
  async requestPreview(pluginId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/plugins/${pluginId}/request-preview${query ? '?' + query : ''}`);
  },

  // A push plugin's inbox: {deliveries, pending}. `params`: {limit}.
  async getDeliveries(pluginId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/plugins/${pluginId}/deliveries${query ? '?' + query : ''}`);
  },

  // The runs feed: {runs, total, counts, limit, offset}. `params`: plugin_id,
  // outcome, tool_id, started_by, status, limit, offset — and group_id, which
  // reads the group's feed instead of your own.
  async getPluginRuns(params = {}) {
    const { group_id: groupId, ...rest } = params;
    const clean = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const query = new URLSearchParams(clean).toString();
    const base = groupId ? `/groups/${encodeURIComponent(groupId)}/plugin-runs` : '/plugin-runs';
    return this.request(`${base}${query ? '?' + query : ''}`);
  },

  // A plugin is a third_parties row, so its versions and rollback are the app
  // routes: {current_version, versions, schema_names}.
  async getVersions(pluginId) {
    return this.request(`/third-party/${pluginId}/versions`);
  },

  async rollbackVersion(pluginId, version, epAction) {
    const body = { version };
    if (epAction) body.ep_action = epAction;
    return this.request(`/third-party/${pluginId}/rollback`, {
      method: 'POST',
      body,
    });
  },

  // Exactly one of: {to_user_id} | {to_username_or_email} | {to_group_id} |
  // {to_personal: true}. The same route as an app's transfer.
  async transferPlugin(pluginId, target) {
    return this.request(`/third-party/${pluginId}/transfer`, {
      method: 'POST',
      body: target,
    });
  },
};
